import { Injectable } from '@angular/core';
import { IMessageHistory } from '@dxp/core/models';
import JSZip from 'jszip';
import { from, map, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ChatTranscriptService {
    public createTranscript(
        conversationId: string,
        chatMessages: IMessageHistory[],
    ): Observable<{ isZip: boolean; blob: Blob }> {
        const messagesWithAttachment = chatMessages.filter(
            m => m.attachments?.length !== 0,
        );
        const messagesBlob = this.#createMessagesBlob(
            conversationId,
            chatMessages,
        );

        if (messagesWithAttachment.length === 0) {
            return from([{ isZip: false, blob: messagesBlob }]);
        }

        return this.#zipAttachments(messagesBlob, messagesWithAttachment).pipe(
            map(zipBlob => ({
                isZip: true,
                blob: zipBlob,
            })),
        );
    }

    #createMessagesBlob(
        conversationId: string,
        chatMessages: IMessageHistory[],
    ) {
        const title = `Downloaded at: ${new Date().toLocaleString()}\r\n`;
        const conversationInfo = `Conversation Id: ${conversationId}\r\n\r\n`;

        const orderedChatMessages = [...chatMessages]
            .filter(m => m.messageBody || m.attachments)
            .sort((a, b) => a.createdAt.localeCompare(b.createdAt));

        const formattedMessages = orderedChatMessages.map(m => {
            const date = new Date(m.createdAt).toLocaleString();
            const line = `[${date}] ${m.from}\r\n`;

            if (m.messageBody) return line + `${m.messageBody}\r\n\r\n`;
            if (m.attachments) return line + `${m.attachments[0].name}\r\n\r\n`;

            return line;
        });

        return new Blob([title, conversationInfo, ...formattedMessages], {
            type: 'text/plain;charset=utf-8',
        });
    }

    #zipAttachments(
        messagesBlob: Blob,
        messagesWithAttachment: IMessageHistory[],
    ) {
        const zip = new JSZip();
        zip.file('transcript.txt', messagesBlob);
        const attachmentsFolder = zip.folder('attachments');

        const attachmentPromises = messagesWithAttachment.map(async m => {
            if (!m.attachments || !m.attachments[0]) return;

            const response = await fetch(m.attachments[0].contentUrl);
            const blob = await response.blob();
            attachmentsFolder?.file(m.attachments[0].name, blob);
        });

        return from(
            Promise.all(attachmentPromises).then(() =>
                zip.generateAsync({ type: 'blob' }),
            ),
        );
    }
}
